import { useEffect, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { isLdConfigured } from '@/lib/launchdarkly/config';
import { useExperimentOverride } from './overrideStore';

/**
 * Experiment flag conventions (`exp.<area>.<name>`, e.g. `exp.ftue.experience`):
 *
 * - Multivariate flag; each variation is either a plain **string** (the
 *   variant key) or a JSON object `{ "variant": "...", "config": {...} }`
 *   when the variant carries tunable parameters.
 * - Variant keys are snake/kebab identifiers; `control` is reserved for the
 *   baseline and `__excluded` for "not in this experiment" (mirrors the
 *   monetization-experience sentinel). Excluded users render control and
 *   fire NO exposure — targeting who is *in* the experiment stays pure
 *   LaunchDarkly configuration.
 * - Percentage rollouts must bucket by the **device** context kind so
 *   assignment survives login (see `LaunchDarklyProvider`).
 * - Flags must be enabled for client-side SDKs in the LD dashboard.
 */
export const CONTROL_VARIANT = 'control';
export const EXCLUDED_VARIANT = '__excluded';

/**
 * How long we hold rendering waiting for LaunchDarkly before giving up and
 * serving control. LD streaming init is typically <300ms; this only bites on
 * degraded networks. Deliberately much shorter than the provider's 10s init
 * timeout — an experiment is never worth holding the lobby hostage.
 */
const DEFAULT_READY_TIMEOUT_MS = 2000;

export interface ExperimentEvaluation {
  /** Assigned variant key (`control` when unassigned/fallback). */
  variant: string;
  /** Optional JSON payload config for the variant (undefined for string flags). */
  config: unknown;
  /** False while LD is still initializing — render a fallback, not control. */
  ready: boolean;
  /**
   * True when the value did NOT come from a real flag evaluation (LD
   * unconfigured, init timed out, flag missing, or malformed value).
   * Rides on the exposure event so analysis can exclude fallback traffic.
   */
  isFallback: boolean;
  /**
   * True when the variant was forced via the URL (`?<flagKey>=<variant>`) —
   * the QA path for previewing an arm before the flag ramps. Overridden
   * sessions fire NO exposure and stamp NO super-property: QA traffic must
   * never pollute the experiment data. Harmless to expose: it only selects
   * among components already shipped in the bundle.
   */
  isOverride: boolean;
}

/** Normalize a raw LD flag value into `{variant, config}`, or null when unusable. */
function parseFlagValue(raw: unknown): { variant: string; config: unknown } | null {
  if (typeof raw === 'string' && raw.length > 0) return { variant: raw, config: undefined };
  if (raw !== null && typeof raw === 'object') {
    const candidate = raw as { variant?: unknown; config?: unknown };
    if (typeof candidate.variant === 'string' && candidate.variant.length > 0) {
      return { variant: candidate.variant, config: candidate.config };
    }
  }
  return null;
}

/**
 * Evaluate an `exp.*` experiment flag.
 *
 * Resolution order:
 * 1. LD not configured → control immediately (fallback).
 * 2. Flag evaluated → its variant.
 * 3. LD init settled (`useLDClient()` defined) but flag absent/malformed →
 *    control (fallback) — the flag doesn't exist or isn't client-side.
 * 4. LD still initializing → not ready; after {@link DEFAULT_READY_TIMEOUT_MS}
 *    we stop waiting and serve control (fallback).
 */
export function useExperiment(flagKey: string, readyTimeoutMs: number = DEFAULT_READY_TIMEOUT_MS): ExperimentEvaluation {
  const flags = useFlags();
  const ldClient = useLDClient();
  const configured = isLdConfigured();
  // Reactive: seeded from the URL, live-updated by the experiments devtool.
  const override = useExperimentOverride(flagKey);
  const settled = !configured || ldClient !== undefined;
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (override !== null || settled || timedOut) return;
    const timer = setTimeout(() => setTimedOut(true), readyTimeoutMs);
    return () => clearTimeout(timer);
  }, [override, settled, timedOut, readyTimeoutMs]);

  if (override !== null) {
    return { variant: override, config: undefined, ready: true, isFallback: true, isOverride: true };
  }

  const parsed = configured ? parseFlagValue(flags[flagKey]) : null;
  if (parsed) {
    return { variant: parsed.variant, config: parsed.config, ready: true, isFallback: false, isOverride: false };
  }

  return {
    variant: CONTROL_VARIANT,
    config: undefined,
    ready: settled || timedOut,
    isFallback: true,
    isOverride: false,
  };
}
